import React from "react";
import {
  CheckCircle, XCircle, Image, FileText, BarChart3, Star,
  Users, TrendingUp, HelpCircle, AlertTriangle, ArrowRight
} from "lucide-react";

const GMAPS_VIEW = "https://www.google.com/maps/place/?q=place_id:ChIJ8Zku0V47KhMRb5aqDGqIbU0";
const WA_LINK = "https://wa.me/393420380578?text=Ciao%20Riccardo!%20Vorrei%20ottimizzare%20la%20mia%20scheda%20Google";

// Green efficacy ring SVG
const EfficaciaRing = ({ pct, size = 56 }) => {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#34A853" strokeWidth="5"
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2+1} textAnchor="middle" dominantBaseline="middle"
        fill="white" fontSize="13" fontWeight="900">{pct}%</text>
    </svg>
  );
};

const THEIR_CHECKS = [
  { icon: CheckCircle, text: "Profilo verificato e completo", color: "#34A853" },
  { icon: Star, text: "5,0 stelle — 51 recensioni reali", color: "#FBBC04" },
  { icon: Users, text: "237 interazioni con i clienti", color: "#4285F4" },
  { icon: Image, text: "Foto pertinenti e aggiornate", color: "#34A853" },
  { icon: FileText, text: "Post e aggiornamenti regolari", color: "#4285F4" },
  { icon: BarChart3, text: "Rendimento monitorato", color: "#FBBC04" },
];

const CLIENT_FAILS = [
  { icon: XCircle, text: "Non sanno fare Login", color: "#EA4335" },
  { icon: XCircle, text: "Profilo incompleto o sospeso", color: "#EA4335" },
  { icon: XCircle, text: "Zero post, zero aggiornamenti", color: "#EA4335" },
  { icon: XCircle, text: "Foto mancanti o irrilevanti", color: "#EA4335" },
  { icon: XCircle, text: "Recensioni senza risposta", color: "#EA4335" },
  { icon: XCircle, text: "Nessun monitoraggio rendimento", color: "#EA4335" },
];

const ProofSection = () => (
  <section className="py-16 bg-[#080c18] relative overflow-hidden" data-testid="proof-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10">
        <h2 className="text-2xl md:text-3xl font-black text-white mb-2" style={{ fontFamily: "'Poppins',sans-serif" }}>
          La <span className="text-[#34A853]">nostra</span> scheda vs la <span className="text-[#EA4335]">tua</span>
        </h2>
        <p className="text-gray-400 text-sm max-w-lg mx-auto">
          I fatti. Nient'altro.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
        {/* LEFT — La nostra scheda (100%) */}
        <div className="bg-white/[0.03] border border-[#34A853]/20 rounded-2xl p-6" data-testid="proof-ours">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-white font-black text-base">schedabusiness.com</p>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-white font-bold text-sm">5,0</span>
                <span className="text-yellow-400 text-sm">★★★★★</span>
                <a href={GMAPS_VIEW} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-[11px] hover:underline ml-1">51 recensioni</a>
              </div>
            </div>
            <div className="flex flex-col items-center">
              <EfficaciaRing pct={100} />
              <span className="text-[#34A853] text-[9px] font-bold mt-1">Efficacia profilo</span>
            </div>
          </div>

          <div className="space-y-2">
            {THEIR_CHECKS.map(item => {
              const Icon = item.icon;
              return (
                <div key={item.text} className="flex items-center gap-2.5">
                  <Icon className="w-4 h-4 flex-shrink-0" style={{ color: item.color }} />
                  <span className="text-gray-300 text-sm">{item.text}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-5 pt-4 border-t border-white/5 flex items-center gap-3">
            <div className="flex -space-x-1">
              {["#4285F4", "#EA4335", "#FBBC04", "#34A853"].map(c => (
                <div key={c} className="w-6 h-6 rounded-full border-2 border-[#080c18]" style={{ background: c }} />
              ))}
            </div>
            <span className="text-gray-500 text-[11px]">Post, foto, aggiornamenti attivi</span>
          </div>
        </div>

        {/* RIGHT — La loro scheda (problematica) */}
        <div className="bg-white/[0.03] border border-[#EA4335]/20 rounded-2xl p-6" data-testid="proof-theirs">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-white/40 font-black text-base">La tua attivita'?</p>
              <div className="flex items-center gap-1 mt-0.5">
                <HelpCircle className="w-3.5 h-3.5 text-gray-600" />
                <span className="text-gray-600 text-sm">Nessun dato</span>
              </div>
            </div>
            <div className="flex flex-col items-center">
              <EfficaciaRing pct={0} />
              <span className="text-[#EA4335] text-[9px] font-bold mt-1">Efficacia profilo</span>
            </div>
          </div>

          <div className="space-y-2">
            {CLIENT_FAILS.map(item => {
              const Icon = item.icon;
              return (
                <div key={item.text} className="flex items-center gap-2.5">
                  <Icon className="w-4 h-4 flex-shrink-0" style={{ color: item.color }} />
                  <span className="text-gray-500 text-sm">{item.text}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-5 pt-4 border-t border-white/5">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#EA4335]" />
              <span className="text-[#EA4335] text-xs font-bold">Ogni giorno perdi clienti</span>
            </div>
          </div>
        </div>
      </div>

      {/* CTA bottom */}
      <div className="text-center mt-8">
        <a
          href={WA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#34A853] hover:bg-[#2d9249] text-white rounded-full px-7 py-3 font-black text-sm transition-all hover:scale-105"
          data-testid="proof-cta"
        >
          Porta la tua scheda al 100% <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  </section>
);

export default ProofSection;
